import { memo, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import InputAdornment from "@mui/material/InputAdornment";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import FilterListIcon from "@mui/icons-material/FilterList";
import ViewModuleOutlinedIcon from "@mui/icons-material/ViewModuleOutlined";
import TableRowsOutlinedIcon from "@mui/icons-material/TableRowsOutlined";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import { getStatusUi } from "../utils/messageTemplateUiUtils";

export type MessageTemplatesViewMode = "cards" | "table";

export interface MessageTemplatesToolbarProps {
  searchQuery: string;
  onChangeSearchQuery: (value: string) => void;
  viewMode: MessageTemplatesViewMode;
  onChangeViewMode: (mode: MessageTemplatesViewMode) => void;
  onCreateTemplate?: () => void;
  showCreateButton?: boolean;
  availableStatuses: string[];
  selectedStatuses: string[];
  onChangeSelectedStatuses: (statuses: string[]) => void;
  onRefreshTemplates?: () => void | Promise<void>;
  isRefreshing?: boolean;
}

const MessageTemplatesToolbar = memo(
  ({
    searchQuery,
    onChangeSearchQuery,
    viewMode,
    onChangeViewMode,
    onCreateTemplate,
    showCreateButton = true,
    availableStatuses,
    selectedStatuses,
    onChangeSelectedStatuses,
    onRefreshTemplates,
    isRefreshing = false,
  }: MessageTemplatesToolbarProps) => {
    const [statusAnchorEl, setStatusAnchorEl] = useState<null | HTMLElement>(
      null,
    );
    const isStatusMenuOpen = Boolean(statusAnchorEl);

    const orderedStatuses = useMemo(() => {
      const all = availableStatuses.filter(Boolean);
      return [...all].sort((a, b) => a.localeCompare(b));
    }, [availableStatuses]);

    const toggleStatus = (status: string) => {
      const isSelected = selectedStatuses.includes(status);
      if (isSelected) {
        onChangeSelectedStatuses(selectedStatuses.filter((s) => s !== status));
        return;
      }
      onChangeSelectedStatuses([...selectedStatuses, status]);
    };

    return (
      <Box sx={{ mb: 1.5, mt: 1.5 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ md: "center" }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <TextField
            fullWidth
            variant="filled"
            label="Filtrar modelos"
            placeholder="Busque por nome ou conteúdo do modelo"
            value={searchQuery}
            onChange={(event) => onChangeSearchQuery(event.target.value)}
            sx={{
              maxWidth: { md: 520 },
              "& .MuiFilledInput-root": {
                bgcolor: "background.paper",
              },
              "& .MuiFilledInput-root:hover": {
                bgcolor: "background.paper",
              },
              "& .MuiFilledInput-root.Mui-focused": {
                bgcolor: "background.paper",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="disabled" />
                </InputAdornment>
              ),
            }}
          />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="flex-end"
            sx={{ flexShrink: 0 }}
          >
            {onRefreshTemplates ? (
              <Tooltip title="Atualizar lista de modelos">
                <span>
                  <Button
                    variant="outlined"
                    size="medium"
                    aria-label="Atualizar lista de modelos"
                    disabled={isRefreshing}
                    onClick={() => void onRefreshTemplates()}
                    sx={{
                      height: 36,
                      minWidth: 36,
                      px: 1.25,
                      borderRadius: 2,
                      bgcolor: "background.paper",
                    }}
                  >
                    {isRefreshing ? (
                      <CircularProgress color="inherit" size={18} />
                    ) : (
                      <RefreshIcon fontSize="small" />
                    )}
                  </Button>
                </span>
              </Tooltip>
            ) : null}
            <Button
              variant="outlined"
              size="medium"
              startIcon={<FilterListIcon />}
              sx={{
                height: 36,
                px: 2,
                borderRadius: 2,
                textTransform: "none",
                whiteSpace: "nowrap",
                bgcolor: "background.paper",
              }}
              onClick={(event) => setStatusAnchorEl(event.currentTarget)}
            >
              Status
              {selectedStatuses.length > 0
                ? ` (${selectedStatuses.length})`
                : ""}
            </Button>
            <Box
              sx={{
                display: "inline-flex",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                p: 0.25,
                bgcolor: "background.paper",
              }}
            >
              <ToggleButtonGroup
                exclusive
                value={viewMode}
                onChange={(
                  _event,
                  nextMode: MessageTemplatesViewMode | null,
                ) => {
                  if (!nextMode) return;
                  onChangeViewMode(nextMode);
                }}
                sx={{
                  height: 32,
                  "& .MuiToggleButton-root": {
                    textTransform: "none",
                    px: 1.5,
                    border: "none",
                    borderRadius: 2,
                    color: "text.secondary",
                    backgroundColor: "transparent",
                  },
                  "& .MuiToggleButton-root.Mui-selected": {
                    color: "primary.main",
                    backgroundColor: "action.hover",
                    fontWeight: 600,
                  },
                }}
              >
                <ToggleButton value="cards">
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <ViewModuleOutlinedIcon fontSize="small" />
                    <span>Cards</span>
                  </Stack>
                </ToggleButton>
                <ToggleButton value="table">
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <TableRowsOutlinedIcon fontSize="small" />
                    <span>Tabela</span>
                  </Stack>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Menu
              anchorEl={statusAnchorEl}
              open={isStatusMenuOpen}
              onClose={() => setStatusAnchorEl(null)}
            >
              {orderedStatuses.length === 0 ? (
                <MenuItem disabled>Sem status disponível</MenuItem>
              ) : (
                orderedStatuses.map((status) => (
                  <MenuItem
                    key={status}
                    onClick={() => toggleStatus(status)}
                    dense
                  >
                    <Checkbox checked={selectedStatuses.includes(status)} />
                    <ListItemText primary={getStatusUi(status).label} />
                  </MenuItem>
                ))
              )}
              {selectedStatuses.length > 0 && (
                <MenuItem
                  onClick={() => onChangeSelectedStatuses([])}
                  sx={{ color: "primary.main", fontWeight: 600 }}
                >
                  Limpar filtros
                </MenuItem>
              )}
            </Menu>

            {showCreateButton && onCreateTemplate ? (
              <Button
                variant="contained"
                size="medium"
                startIcon={<AddIcon />}
                sx={{
                  height: 36,
                  px: 2,
                  borderRadius: 2,
                  textTransform: "none",
                  whiteSpace: "nowrap",
                }}
                onClick={onCreateTemplate}
              >
                Novo modelo
              </Button>
            ) : null}
          </Stack>
        </Stack>
      </Box>
    );
  },
);

MessageTemplatesToolbar.displayName = "MessageTemplatesToolbar";

export default MessageTemplatesToolbar;
